import React, { useEffect, useMemo, useRef, useState } from 'react';
import useRecordingReason from '../recording/useRecordingReason';
import { mediaBlob, renameSpeakers, reasonOf, clockOf } from '../../lib/recordings';
import { Icon } from '../todos/icons';
import './RoomRecorder.css';

/**
 * WHO IS SPEAKER 1? (Gordon, 22 October: "the app distinguishes voices as
 * Person 1, Person 2, etc so the organiser can assign names later.")
 *
 * One recording device on the table means the transcriber knows the voices
 * apart but not whose they are. So, for each voice it found: a Play button
 * that plays that voice's first few lines from the recording itself, its
 * longest line as a quote (the quickest way to recognise somebody is what
 * they said), and a picker of the names the organiser listed as being in the
 * room - with "somebody else" for a name that was not. Each pick is sent at
 * once (PATCH /recordings/:id/speakers { map }), so there is nothing to lose
 * by wandering off half-way; "Looks right" is the way to say you are done.
 *
 * Built for a thumb: one voice per row, the controls a finger wide, and the
 * media fetched only when Play is first pressed, because a phone on mobile
 * data should not download an hour of audio to show a list of four names.
 *
 *   recording    { id, segments: [{ seq, start_ms, end_ms, speaker, speaker_id, text }],
 *                  attendees: [{ key, name }] }  - the GET /recordings/:id shape
 *   mediaUrl     an object URL the page already has; without one the media is
 *                fetched here on the first Play
 *   onRenamed    (map) after every accepted PATCH, with the cumulative map, so
 *                the page can relabel its own transcript
 *   onDone       pressed "Looks right" (optional)
 *
 * The map is keyed the way RecordingDetail keys it: by the transcriber's own
 * speaker_id where a segment carries one, by the label otherwise.
 */

const GENERIC = /^(Speaker|Person|Voice)\s*\d+$/i;
const PLAY_LINES = 3;        // how many of a voice's lines Play plays
const LINE_CAP_MS = 8000;    // and no line for longer than this
const OTHER = '__other__';

/** The voices, in order of first appearance, with what each said. */
export const voicesOf = (segments) => {
  const by = new Map();
  for (const s of (segments || []).slice().sort((a, b) => (a.start_ms || 0) - (b.start_ms || 0))) {
    const key = s.speaker_id || s.speaker || 'Unknown';
    if (!by.has(key)) by.set(key, { key, label: s.speaker || 'Unknown', lines: [], longest: s });
    const v = by.get(key);
    v.lines.push(s);
    if (String(s.text || '').length > String(v.longest.text || '').length) v.longest = s;
  }
  return [...by.values()];
};

const quoteOf = (text) => {
  const t = String(text || '').trim();
  return t.length > 160 ? `${t.slice(0, 157).trimEnd()}…` : t;
};

export default function SpeakerNaming({ recording, mediaUrl = null, onRenamed = null, onDone = null }) {
  const voices = useMemo(() => voicesOf(recording?.segments), [recording?.segments]);
  const attendees = useMemo(() => (recording?.attendees || []).map((a) => (typeof a === 'string' ? a : a?.name))
    .filter(Boolean), [recording?.attendees]);
  // What has been chosen here, by voice key, over what the segments say.
  const [chosen, setChosen] = useState({});
  const [typing, setTyping] = useState({});   // voice key -> free text being typed
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [playing, setPlaying] = useState(null); // { key, i }
  const [url, setUrl] = useState(mediaUrl);
  const [fetching, setFetching] = useState(false);
  const [mediaTrouble, setMediaTrouble] = useState(null);
  // Not on the call: the reason first, once a day (3 Nov) - asked on the
  // recording's page already, so in practice only here from elsewhere.
  const [withReason, reasonDialog] = useRecordingReason();
  const audioRef = useRef(null);
  const ownUrl = useRef(null);
  // What each voice was called when first seen. A rename reaches the page's
  // segments too, so without this the row would read "Rita Gatt → Rita Gatt".
  const firstLabels = useRef({});
  for (const v of voices) {
    if (!firstLabels.current[v.key]) firstLabels.current[v.key] = v.label;
    v.label = firstLabels.current[v.key];
  }

  useEffect(() => { if (mediaUrl) setUrl(mediaUrl); }, [mediaUrl]);
  useEffect(() => () => { if (ownUrl.current) URL.revokeObjectURL(ownUrl.current); }, []);

  const nameOf = (v) => chosen[v.key] || (GENERIC.test(v.label) ? '' : v.label);
  const named = voices.filter((v) => nameOf(v)).length;

  /* ---- naming */
  const name = async (v, to) => {
    const value = String(to || '').trim();
    if (!value || value === nameOf(v)) return;
    const before = chosen;
    const next = { ...chosen, [v.key]: value };
    setChosen(next);
    setBusy(v.key);
    setError(null);
    try {
      await renameSpeakers(recording.id, { [v.key]: value });
      if (onRenamed) onRenamed(Object.fromEntries(voices.filter((x) => next[x.key]).map((x) => [x.key, next[x.key]])));
    } catch (err) {
      setChosen(before);
      setError(reasonOf(err, `Could not name ${v.label}.`));
    } finally {
      setBusy(null);
    }
  };

  const pick = (v) => (e) => {
    const value = e.target.value;
    if (value === OTHER) { setTyping((t) => ({ ...t, [v.key]: t[v.key] ?? '' })); return; }
    setTyping((t) => { const n = { ...t }; delete n[v.key]; return n; });
    if (value) name(v, value);
  };

  /* ---- playing: this voice's first lines, one after another */
  const stopPlaying = () => {
    const el = audioRef.current;
    if (el) { try { el.pause(); } catch { /* fine */ } }
    setPlaying(null);
  };

  const ensureMedia = async () => {
    if (url) return url;
    if (!recording?.id) return null;
    setFetching(true);
    try {
      const blob = await withReason(recording.id, () => mediaBlob(recording.id), 'audio');
      const u = URL.createObjectURL(blob);
      ownUrl.current = u;
      setUrl(u);
      return u;
    } catch (err) {
      setMediaTrouble(err.response?.status === 404 ? 'The recording itself has gone; the transcript stays.'
        : reasonOf(err, 'The recording could not be loaded.'));
      return null;
    } finally {
      setFetching(false);
    }
  };

  const playLine = (v, i) => {
    const el = audioRef.current;
    const line = v.lines[i];
    if (!el || !line) { setPlaying(null); return; }
    setPlaying({ key: v.key, i });
    el.currentTime = Math.max(0, (line.start_ms || 0) / 1000);
    el.play().catch(() => setPlaying(null));
  };

  const play = async (v) => {
    if (playing?.key === v.key) { stopPlaying(); return; }
    const u = await ensureMedia();
    if (!u) return;
    // The element may have just been given its src; give it a tick.
    setTimeout(() => playLine(v, 0), 0);
  };

  const onTime = (e) => {
    if (!playing) return;
    const v = voices.find((x) => x.key === playing.key);
    const line = v?.lines[playing.i];
    if (!v || !line) { stopPlaying(); return; }
    const ms = e.currentTarget.currentTime * 1000;
    const end = Math.min(line.end_ms || Infinity, (line.start_ms || 0) + LINE_CAP_MS);
    if (ms >= end) {
      if (playing.i + 1 < Math.min(PLAY_LINES, v.lines.length)) playLine(v, playing.i + 1);
      else stopPlaying();
    }
  };

  if (!voices.length) return null;

  return (
    <section className="rs" aria-labelledby="rs-h" data-named={named} data-total={voices.length}>
      {reasonDialog}
      <header className="rs-head">
        <h2 className="rs-h" id="rs-h">Who is who?</h2>
        <span className="rs-progress" role="status" aria-live="polite">{named} of {voices.length} named</span>
      </header>
      <p className="rs-hint">
        Press Play to hear a voice, then pick the name. Every one of their lines takes the name, here and for everybody.
      </p>
      {url && (
        <audio ref={audioRef} src={url} preload="none" onTimeUpdate={onTime} onEnded={stopPlaying} />
      )}
      {mediaTrouble && <p className="rs-error" role="alert">{mediaTrouble}</p>}
      {error && (
        <p className="rs-error" role="alert">{error} <button type="button" className="tds-link" onClick={() => setError(null)}>Dismiss</button></p>
      )}

      <ol className="rs-list">
        {voices.map((v) => {
          const current = nameOf(v);
          const isPlaying = playing?.key === v.key;
          const other = Object.prototype.hasOwnProperty.call(typing, v.key);
          const inList = current && attendees.includes(current);
          const selectValue = other ? OTHER : (inList ? current : (current ? OTHER : ''));
          return (
            <li key={v.key} className={`rs-voice${current ? ' is-named' : ''}`} data-voice={v.label}>
              <button type="button" className={`rs-play${isPlaying ? ' is-on' : ''}`} onClick={() => play(v)}
                aria-label={isPlaying ? `Stop ${v.label}` : `Play ${v.label}`} aria-pressed={isPlaying} disabled={fetching || Boolean(mediaTrouble)}>
                <span className={`rs-play-glyph${isPlaying ? ' is-stop' : ''}`} aria-hidden="true" />
              </button>
              <div className="rs-body">
                <p className="rs-who">
                  <span className="rs-label">{v.label}</span>
                  {current && <span className="rs-arrow" aria-hidden="true">→</span>}
                  {current && <strong className="rs-named">{current}</strong>}
                  <span className="rs-meta">{v.lines.length} line{v.lines.length === 1 ? '' : 's'} · from {clockOf(v.lines[0].start_ms)}</span>
                </p>
                {v.longest?.text && <blockquote className="rs-quote">“{quoteOf(v.longest.text)}”</blockquote>}
                <div className="rs-pick">
                  <select className="rs-select" value={selectValue} onChange={pick(v)} aria-label={`Who is ${v.label}?`} disabled={busy === v.key}>
                    <option value="">Who is this?</option>
                    {attendees.map((n) => <option key={n} value={n}>{n}</option>)}
                    {current && !inList && !other && <option value={OTHER}>{current}</option>}
                    <option value={OTHER}>Somebody else…</option>
                  </select>
                  {other && (
                    <form className="rs-other" onSubmit={(e) => { e.preventDefault(); name(v, typing[v.key]); setTyping((t) => { const n = { ...t }; delete n[v.key]; return n; }); }}>
                      <input value={typing[v.key] || ''} onChange={(e) => setTyping((t) => ({ ...t, [v.key]: e.target.value }))}
                        placeholder="Their name" aria-label={`Name for ${v.label}`} autoFocus autoComplete="off" />
                      <button type="submit" className="btn btn-primary tds-btn is-small" disabled={!String(typing[v.key] || '').trim()}>Save</button>
                    </form>
                  )}
                  {busy === v.key && <span className="rs-saving" role="status">Saving…</span>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <footer className="rs-foot">
        {done ? (
          <p className="rs-done" role="status"><Icon name="check" size={16} /> Names saved.</p>
        ) : (
          <button type="button" className="btn btn-primary tds-btn rs-finish" onClick={() => { stopPlaying(); setDone(true); if (onDone) onDone(); }}>
            <Icon name="check" size={16} /> Looks right
          </button>
        )}
        {named < voices.length && !done && (
          <span className="rs-hint rs-foot-hint">{voices.length - named} still to name - fine to leave for later.</span>
        )}
      </footer>
    </section>
  );
}
